import {
  BookOpen,
  Bell,
  CalendarCheck2,
  CircleHelp,
  ClipboardCheck,
  Gift,
  LayoutGrid,
  ListChecks,
  Route,
  Shapes,
  ShieldCheck,
  Trophy,
  UsersRound,
  Zap,
  type LucideIcon,
} from 'lucide-react';

export type PortalSectionIcon =
  | 'book'
  | 'calendar'
  | 'checklist'
  | 'family'
  | 'gift'
  | 'help'
  | 'judging'
  | 'learning'
  | 'notifications'
  | 'route'
  | 'sections'
  | 'tasks'
  | 'trophy'
  | 'zap';

export type PortalSectionTone = 'bubblegum' | 'coral' | 'mint' | 'sky' | 'sunshine';

const ICONS: Record<PortalSectionIcon, LucideIcon> = {
  book: BookOpen,
  calendar: CalendarCheck2,
  checklist: ClipboardCheck,
  family: UsersRound,
  gift: Gift,
  help: CircleHelp,
  judging: ShieldCheck,
  learning: Shapes,
  notifications: Bell,
  route: Route,
  sections: LayoutGrid,
  tasks: ListChecks,
  trophy: Trophy,
  zap: Zap,
};

const TONE_CLASSES: Record<PortalSectionTone, string> = {
  bubblegum: 'bg-wash-bubblegum text-brand-bubblegum',
  coral: 'bg-wash-coral text-brand-coral',
  mint: 'bg-wash-mint text-brand-mint',
  sky: 'bg-white/85 text-brand-sky ring-1 ring-inset ring-brand-sky/20',
  sunshine: 'bg-wash-sunshine text-ink',
};

const SIZE_CLASSES = {
  sm: {
    icon: 'h-9 w-9 rounded-xl',
    glyph: 19,
    heading: 'text-[15px] font-bold leading-tight text-ink',
  },
  md: {
    icon: 'h-11 w-11 rounded-2xl',
    glyph: 23,
    heading: 'text-[22px] font-bold leading-tight text-ink sm:text-[24px]',
  },
  lg: {
    icon: 'h-12 w-12 rounded-2xl',
    glyph: 26,
    heading: 'text-[30px] font-bold leading-none tracking-[-0.025em] text-ink sm:text-[36px]',
  },
} as const;

interface PortalSectionTitleProps {
  title: string;
  icon: PortalSectionIcon;
  id?: string;
  level?: 'h1' | 'h2' | 'h3';
  size?: keyof typeof SIZE_CLASSES;
  tone?: PortalSectionTone;
  className?: string;
  testId?: string;
}

export function PortalSectionTitle({
  title,
  icon,
  id,
  level = 'h2',
  size = 'md',
  tone = 'sky',
  className = '',
  testId,
}: PortalSectionTitleProps) {
  const Icon = ICONS[icon];
  const Heading = level;
  const styles = SIZE_CLASSES[size];

  return (
    <div className={`flex min-w-0 items-center gap-3 ${className}`} data-testid={testId}>
      <span
        className={`grid shrink-0 place-items-center ${styles.icon} ${TONE_CLASSES[tone]}`}
        aria-hidden="true"
        data-testid={testId ? `${testId}-icon` : undefined}
      >
        <Icon size={styles.glyph} strokeWidth={2.15} />
      </span>
      <Heading id={id} className={styles.heading}>
        {title}
      </Heading>
    </div>
  );
}
